import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Users, CheckCircle2, Activity, UserX, Send, Trash2, Flag, Loader2, TrendingUp, Download, Gift, Eye, EyeOff } from 'lucide-react';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { coachAPI, mediaUrl } from '../services/api';
import { CoachVideos } from '../components/CoachVideos';
import { CoachSecrets, CoachReglement } from '../components/CoachContent';
import { CoachGifts } from '../components/CoachGifts';
import { CoachProofs } from '../components/CoachProofs';
import { CoachRoutines } from '../components/CoachRoutines';

const StatCard = ({ icon: Icon, value, label, color }) => (
  <Card className="p-4 rounded-2xl border-red-100 dark:border-red-900/50">
    <Icon className={`w-5 h-5 ${color}`} />
    <p className="text-2xl font-extrabold text-red-950 dark:text-red-50 mt-2">{value}</p>
    <p className="text-xs text-muted-foreground">{label}</p>
  </Card>
);

export default function Coach() {
  const [overview, setOverview] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [reported, setReported] = useState([]);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [promoFilter, setPromoFilter] = useState('');
  const [exporting, setExporting] = useState(false);

  const load = () => {
    coachAPI.overview().then(setOverview).catch(() => {});
    coachAPI.participants().then(setParticipants).catch(() => {});
    coachAPI.reported().then(setReported).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const broadcast = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      await coachAPI.sendMessage({ target: 'all', content: message.trim() });
      setMessage('');
      toast.success('Message envoyé à tous les participants');
    } catch { toast.error("Échec de l'envoi"); }
    finally { setSending(false); }
  };

  const toggleVisibility = async (p) => {
    const hidden = !p.coach_hidden;
    try {
      await coachAPI.setVisibility(p.user_id, hidden);
      setParticipants((list) => list.map((x) => (x.user_id === p.user_id ? { ...x, coach_hidden: hidden } : x)));
      toast.success(hidden ? 'Challenger masqué du classement' : 'Challenger réintégré au classement');
      coachAPI.overview().then(setOverview).catch(() => {});
    } catch { toast.error('Échec de la mise à jour'); }
  };

  const remove = async (id) => {
    await coachAPI.deletePost(id);
    toast.success('Publication supprimée');
    setReported((r) => r.filter((p) => p.id !== id));
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const blob = await coachAPI.exportCsv(promoFilter.trim() || undefined);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'participants_bfc21.csv'; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export CSV téléchargé');
    } catch { toast.error("Échec de l'export"); }
    finally { setExporting(false); }
  };

  const filteredParticipants = promoFilter.trim()
    ? participants.filter((p) => (p.promo_code || '').toLowerCase() === promoFilter.trim().toLowerCase())
    : participants;

  if (!overview) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-red-600" /></div>;

  return (
    <div className="space-y-6 animate-rise">
      <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-red-950 dark:text-red-50">Espace Coach</h1>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <StatCard icon={Users} value={overview.total_participants} label="Participants" color="text-red-600" />
        <StatCard icon={CheckCircle2} value={overview.validated_today} label="Défis validés aujourd'hui" color="text-rose-600" />
        <StatCard icon={Activity} value={overview.active_users} label="Actifs" color="text-orange-500" />
        <StatCard icon={UserX} value={overview.inactive_users} label="Inactifs (2j+)" color="text-red-500" />
        <StatCard icon={TrendingUp} value={`${overview.avg_progress_percent}%`} label="Progression moyenne" color="text-red-600" />
        <StatCard icon={Send} value={overview.posts_today} label="Publications du jour" color="text-rose-600" />
      </div>

      {/* Broadcast */}
      <Card className="p-5 rounded-2xl border-red-100 dark:border-red-900/50">
        <h3 className="font-bold text-red-950 dark:text-red-50 mb-3">Message à tous les participants</h3>
        <Textarea value={message} onChange={(e) => setMessage(e.target.value)} data-testid="coach-message-input" rows={2} className="rounded-xl resize-none" placeholder="Un mot de motivation pour toute la communauté..." />
        <Button onClick={broadcast} disabled={sending} data-testid="coach-send-message" className="mt-3 bg-orange-500 hover:bg-orange-600 rounded-full font-semibold">
          {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}Envoyer à tous
        </Button>
      </Card>

      {/* Reported posts */}
      {reported.length > 0 && (
        <Card className="p-5 rounded-2xl border-red-100 dark:border-red-900/40">
          <h3 className="font-bold text-red-600 mb-3 flex items-center gap-2"><Flag className="w-4 h-4" />Publications signalées</h3>
          <div className="space-y-3">
            {reported.map((p) => (
              <div key={p.id} className="flex items-start gap-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-xl">
                <p className="flex-1 text-sm text-foreground">{p.content || '(média)'}</p>
                <Button size="sm" variant="ghost" onClick={() => remove(p.id)} data-testid={`delete-post-${p.id}`} className="text-red-600 shrink-0"><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Videos management */}
      <CoachVideos />

      {/* Secret presentations + rules */}
      <CoachSecrets />
      <CoachReglement />

      {/* Gifts, tiers & challenge start date */}
      <CoachGifts />

      {/* Routines completion today */}
      <CoachRoutines />

      {/* Proofs review & point adjustments */}
      <CoachProofs />

      {/* Participants */}
      <Card className="p-5 rounded-2xl border-red-100 dark:border-red-900/50">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h3 className="font-bold text-red-950 dark:text-red-50">Participants</h3>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Gift className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-muted-foreground" />
              <Input value={promoFilter} onChange={(e) => setPromoFilter(e.target.value)} data-testid="promo-filter" placeholder="Filtrer par code promo" className="pl-8 rounded-full h-9 text-sm w-48" />
            </div>
            <Button size="sm" variant="outline" onClick={exportCsv} disabled={exporting} data-testid="export-csv" className="rounded-full font-semibold">
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-1" />CSV</>}
            </Button>
          </div>
        </div>
        <div className="space-y-2" data-testid="participants-list">
          {filteredParticipants.map((p) => (
            <div key={p.user_id} className={`flex items-center gap-3 p-2.5 rounded-xl border border-border ${p.coach_hidden ? 'opacity-50' : ''}`} data-testid={`participant-${p.user_id}`}>
              <Avatar className="w-9 h-9"><AvatarImage src={mediaUrl(p.avatar)} /><AvatarFallback className="bg-red-600 text-white text-xs">{p.name?.charAt(0)}</AvatarFallback></Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-sm text-foreground truncate">{p.name}</p>
                  <Badge className={`text-[9px] border-0 ${p.profile === 'distributeur' ? 'bg-sky-500/20 text-sky-600' : 'bg-orange-500/20 text-orange-600'}`}>{p.profile === 'distributeur' ? 'Distributeur' : 'Client'}</Badge>
                  {p.coach_hidden && <Badge className="text-[9px] border-0 bg-neutral-500/20 text-neutral-500">Masqué</Badge>}
                </div>
                <p className="text-xs text-muted-foreground">{p.validated_count}/21 · 🔥{p.current_streak} · {p.total_points} pts challenge</p>
                <p className="text-[11px] text-muted-foreground">Routines : {p.client_routine_points || 0} client{p.profile === 'distributeur' ? ` · ${p.distrib_routine_points || 0} distrib` : ''}</p>
                {(p.promo_code || p.inviter_name) && (
                  <p className="text-[11px] text-muted-foreground truncate">
                    {p.promo_code && <span className="inline-flex items-center gap-0.5"><Gift className="w-3 h-3" />{p.promo_code}</span>}
                    {p.promo_code && p.inviter_name && ' · '}
                    {p.inviter_name && <span>invité par {p.inviter_name}</span>}
                  </p>
                )}
              </div>
              {p.inactive ? (
                <Badge className="bg-red-100 text-red-700 border-red-200 text-[10px]">Inactif</Badge>
              ) : (
                <Badge className="bg-red-600 text-white border-red-600 text-[10px]">Actif</Badge>
              )}
              <Button
                size="icon" variant="ghost"
                onClick={() => toggleVisibility(p)}
                data-testid={`toggle-visibility-${p.user_id}`}
                title={p.coach_hidden ? 'Réintégrer au classement' : 'Masquer du classement'}
                className="shrink-0 text-muted-foreground hover:text-red-600"
              >
                {p.coach_hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
